import { Action, createReducer, on } from '@ngrx/store';

import { Pagination, User } from '@interfaces';
import * as usersActions from './users.actions';
import { defaultPagination, initialState, UsersState } from './users.state';

const reducer = createReducer(
  initialState,
  on(
    usersActions.getEmployees,
    usersActions.searchForeigners,
    usersActions.getCompanyEmployees,
    usersActions.getUsers,
    state => ({
      ...state,
      loading: true,
      errorMessage: null,
      loadedUsersData: [],
    })
  ),

  on(usersActions.getUsersSuccess, (state, { users }) => ({
    ...state,
    loading: false,
    errorMessage: null,
    loadedUsersData: users.data,
    loadedUsersPagination: users.pagination,
  })),
  on(usersActions.getUsersError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
    loadedUsersData: [],
    loadedUsersPagination: { ...(defaultPagination as Pagination) },
  })),

  on(usersActions.getForeignerBasicData, state => ({
    ...state,
    loading: true,
    errorMessage: null,
  })),
  on(usersActions.getForeignerBasicDataSuccess, (state, { foreigner }) => ({
    ...state,
    foreignerBasicData: foreigner,
    loading: false,
    errorMessage: null,
  })),
  on(usersActions.getForeignerBasicDataError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),
  on(usersActions.resetLoadedForeignerBasicData, state => ({
    ...state,
    foreignerBasicData: null,
  })),

  on(usersActions.getEmployeeBasicData, state => ({
    ...state,
    loading: true,
    errorMessage: null,
  })),
  on(usersActions.getEmployeeBasicDataSuccess, (state, { employee }) => ({
    ...state,
    employeeBasicData: employee,
    loading: false,
    errorMessage: null,
  })),
  on(usersActions.getEmployeeBasicDataError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),
  on(usersActions.resetLoadedEmployeeBasicData, state => ({
    ...state,
    employeeBasicData: null,
  })),

  on(usersActions.createForeigner, usersActions.createEmployee, state => ({
    ...state,
    errorMessage: null,
    loading: true,
  })),
  on(usersActions.createUserSuccess, (state, { createdUser }) => ({
    ...state,
    errorMessage: null,
    loading: false,
    loadedUserData: createdUser,
  })),
  on(usersActions.createUserError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),

  on(usersActions.updateEmployee, usersActions.updateForeigner, state => ({
    ...state,
    errorMessage: null,
    loading: true,
  })),
  on(usersActions.updateUserSuccess, (state, { updatedUser }) => ({
    ...state,
    errorMessage: null,
    loading: false,
    loadedUserData: updatedUser,
    // if updated user is the foreigner basic data - also update it
    foreignerBasicData: state.foreignerBasicData?.id === updatedUser.id ? { ...updatedUser } : state.foreignerBasicData,
    // if updated user on loaded users list - update it
    loadedUsersData: state.loadedUsersData.map(user => (user.id === updatedUser.id ? updatedUser : user)),
  })),
  on(usersActions.updateUserError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),
  on(
    usersActions.removeEmployee,
    usersActions.removeForeigner,
    usersActions.restoreForeigner,
    usersActions.restoreUser,
    state => ({
      ...state,
      errorMessage: null,
      loading: true,
    })
  ),
  on(usersActions.removeUserSuccess, (state, { removedUserId }) => ({
    ...state,
    errorMessage: null,
    loading: false,
    // if removed user on list of loaded users - remove it
    loadedUsersData: state.loadedUsersData.filter(user => user.id !== removedUserId),
  })),
  on(usersActions.restoreUserSuccess, (state, { restoredUser }) => ({
    ...state,
    errorMessage: null,
    loading: false,
    // if restored user on list of loaded users - update it
    loadedUsersData: state.loadedUsersData.map(user => {
      return user.id === restoredUser.id ? (restoredUser as User) : user;
    }),
  })),
  on(usersActions.removeUserError, usersActions.restoreUserError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),

  on(usersActions.resendForeignerActivation, usersActions.resendEmployeeActivation, state => ({
    ...state,
    errorMessage: null,
    loading: true,
  })),
  on(usersActions.resendActivationSuccess, state => ({
    ...state,
    errorMessage: null,
    loading: false,
  })),
  on(usersActions.resendActivationError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),

  on(usersActions.getEmployeeTypes, state => ({
    ...state,
    errorMessage: null,
    employeeTypes: null,
    loadingEmployeeTypes: true,
  })),
  on(usersActions.getEmployeeTypesSuccess, (state, { employeeTypes }) => ({
    ...state,
    errorMessage: null,
    loadingEmployeeTypes: false,
    employeeTypes,
  })),
  on(usersActions.getEmployeeTypesError, state => ({
    ...state,
    errorMessage: null,
    loadingEmployeeTypes: false,
  })),

  on(usersActions.selfRegister, state => ({
    ...state,
    errorMessage: null,
    loading: true,
  })),
  on(usersActions.selfRegisterSuccess, state => ({
    ...state,
    errorMessage: null,
    loading: false,
  })),
  on(usersActions.selfRegisterError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loading: false,
  })),
  on(usersActions.getAllRoles, state => ({
    ...state,
    errorMessage: null,
    employeeTypes: null,
    loadingAllRoles: true,
  })),
  on(usersActions.getAllRolesSuccess, (state, { allRoles }) => ({
    ...state,
    errorMessage: null,
    loadingAllRoles: false,
    allRoles,
  })),
  on(usersActions.getAllRolesError, state => ({
    ...state,
    errorMessage: null,
    loadingAllRoles: false,
  })),

  on(usersActions.reassignForeigners, state => ({
    ...state,
    reassignInProgress: true,
  })),
  on(usersActions.reassignForeignersSuccess, state => ({
    ...state,
    reassignInProgress: false,
  })),
  on(usersActions.reassignForeignersError, state => ({
    ...state,
    reassignInProgress: false,
  })),

  on(usersActions.getAllRequestsDataListing, state => ({
    ...state,
    dataListingRequests: null,
    loadingDataListingRequest: true,
  })),
  on(usersActions.getAllRequestsDataListingSuccess, (state, { dataListingRequests }) => ({
    ...state,
    dataListingRequests,
    loadingDataListingRequest: false,
  })),
  on(usersActions.getAllRequestsDataListingError, state => ({
    ...state,
    dataListingRequests: null,
    loadingDataListingRequest: false,
  })),

  on(usersActions.getDataListingRequestsOfUser, state => ({
    ...state,
    dataListingRequests: null,
    loadingDataListingRequest: true,
  })),
  on(usersActions.getDataListingRequestsOfUserSuccess, (state, { dataListingRequests }) => ({
    ...state,
    dataListingRequests,
    loadingDataListingRequest: false,
  })),
  on(usersActions.getDataListingRequestsOfUserError, state => ({
    ...state,
    dataListingRequests: null,
    loadingDataListingRequest: false,
  })),

  on(usersActions.requestDataListingSuccess, (state, { dataListingRequest }) => ({
    ...state,
    dataListingRequests: [dataListingRequest, ...(state.dataListingRequests || [])],
    loadingDataListingRequest: false,
  })),

  on(usersActions.changeDataListingRequestStatusSuccess, (state, { dataListingRequest }) => ({
    ...state,
    dataListingRequests: (state.dataListingRequests || []).map(request =>
      request.id === dataListingRequest.id ? { ...request, ...dataListingRequest } : request
    ),
    loadingDataListingRequest: false,
  })),

  on(usersActions.modifyUsersTags, state => ({
    ...state,
    loadingUsersTags: true,
    errorMessage: null,
  })),
  on(usersActions.modifyUsersTagsSuccess, (state, { usersTags }) => {
    const updatedUsersData = state.loadedUsersData.map(user => {
      const updated = usersTags.find(u => u.id === user.id);
      if (updated) {
        return { ...user, tags: updated.tags };
      }

      return user;
    });

    const currentForeigner = state.foreignerBasicData;
    const updatedSingle = currentForeigner && usersTags.find(u => u.id === currentForeigner.id);

    return {
      ...state,
      loadingUsersTags: false,
      errorMessage: null,
      loadedUsersData: updatedUsersData,
      foreignerBasicData: updatedSingle ? { ...currentForeigner, tags: updatedSingle.tags } : currentForeigner,
    };
  }),
  on(usersActions.modifyUsersTagsError, (state, { errorMessage }) => ({
    ...state,
    errorMessage,
    loadingUsersTags: false,
    loadedUsersData: [],
  }))
);

export function usersReducer(state: UsersState | undefined, action: Action): UsersState {
  return reducer(state, action);
}
